import React, { useState } from 'react';
import { collection, addDoc, Timestamp, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { kuesionerData } from '../helpers/kuesioner';
import { calculateAgeMonths } from '../helpers/utils';
import { getAssessmentRecommendations } from '../helpers/assessmentRecommendations';
import { evaluateGrowth } from '../helpers/growthEvaluation';
import { Input, Button } from '../components/UI';
import { GpphForm, ChatForm } from '../components/assessment/SpecializedForms';
import TddForm from '../components/assessment/TddForm';
import MataForm from '../components/assessment/MataForm';

// --- Sub-komponen untuk setiap langkah pemeriksaan ---

function PertumbuhanStep({ onNext, child }) {
    const [bb, setBb] = useState('');
    const [tb, setTb] = useState('');
    const [lk, setLk] = useState('');
    
    const handleSubmit = (e) => {
        e.preventDefault();
        
        const weight = parseFloat(bb);
        const height = parseFloat(tb);
        const headCirc = parseFloat(lk);
        const ageInMonths = calculateAgeMonths(child.dob.toDate());
        const gender = child.gender || 'male'; 
        
        const growthEvaluation = evaluateGrowth(weight, height, headCirc, ageInMonths, gender);
        
        const status = growthEvaluation.overall.status;
        const interpretasi = `
Berat Badan: ${growthEvaluation.weight.status} (${growthEvaluation.weight.interpretation})
Tinggi Badan: ${growthEvaluation.height.status} (${growthEvaluation.height.interpretation})
Lingkar Kepala: ${growthEvaluation.headCircumference.status} (${growthEvaluation.headCircumference.interpretation})

Status Keseluruhan: ${growthEvaluation.overall.status}
        `.trim();
        
        const rekomendasi = `
Rekomendasi Berat Badan: ${growthEvaluation.weight.recommendation}
Rekomendasi Tinggi Badan: ${growthEvaluation.height.recommendation}
Rekomendasi Lingkar Kepala: ${growthEvaluation.headCircumference.recommendation}

Rekomendasi Umum: ${growthEvaluation.overall.recommendation}
        `.trim();
        
        const detail = `
BB: ${bb}kg (Z-Score: ${growthEvaluation.weight.zScore || 'N/A'})
TB: ${tb}cm (Deviasi: ${growthEvaluation.height.deviation || 'N/A'}%)
LK: ${lk}cm (Deviasi: ${growthEvaluation.headCircumference.deviation || 'N/A'}%)
Usia: ${ageInMonths} bulan
        `.trim();
        
        // Define urgency based on status
        let urgency = 'low';
        if (status === 'Perlu Rujukan Segera') urgency = 'high';
        else if (status === 'Perlu Perhatian') urgency = 'medium';

        onNext({ 
            pertumbuhan: { 
                bb, tb, lk, status, interpretasi, rekomendasi, detail, urgency, evaluationData: growthEvaluation
            } 
        });
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg mb-4">
                <h4 className="font-semibold text-blue-800 mb-2">Petunjuk Pengukuran</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Berat badan: Timbang tanpa pakaian atau pakaian minimal</li>
                    <li>• Tinggi badan: Ukur dalam posisi berdiri tegak (atau berbaring untuk bayi)</li>
                    <li>• Lingkar kepala: Ukur bagian terlebar kepala dengan pita ukur</li>
                </ul>
            </div>
            
            <Input label="Berat Badan (kg)" type="number" step="0.1" min="0.5" max="50" value={bb} onChange={e => setBb(e.target.value)} required placeholder="Contoh: 12.5" />
            <Input label="Tinggi Badan (cm)" type="number" step="0.1" min="30" max="200" value={tb} onChange={e => setTb(e.target.value)} required placeholder="Contoh: 85.0" />
            <Input label="Lingkar Kepala (cm)" type="number" step="0.1" min="25" max="65" value={lk} onChange={e => setLk(e.target.value)} required placeholder="Contoh: 48.5" />
            <Button type="submit">Lanjut ke Perkembangan</Button>
        </form>
    );
}

function KuesionerStep({ onNext, title, questions, dataKey, nextStepLabel, child }) {
    const [answers, setAnswers] = useState({});

    const handleAnswer = (index, value) => setAnswers(prev => ({ ...prev, [index]: value }));

    const handleSubmit = (e) => {
        e.preventDefault();
        if (Object.keys(answers).length < questions.length) {
            alert("Harap jawab semua pertanyaan.");
            return;
        }
        const score = Object.values(answers).filter(v => v === 'Ya').length;
        const total = questions.length;
        const ageInMonths = calculateAgeMonths(child.dob.toDate());
        
        let assessmentType = dataKey;
        if (dataKey === 'perkembangan') assessmentType = 'kpsp';
        if (dataKey === 'mental') assessmentType = 'kmme';
        
        const recommendations = getAssessmentRecommendations(assessmentType, score, total, ageInMonths);
        
        const assessmentData = {
            status: recommendations.status,
            interpretasi: recommendations.interpretation,
            rekomendasi: recommendations.recommendations.join('\n• '),
            followUp: recommendations.followUp,
            urgency: recommendations.urgency,
            detail: `Skor: ${score}/${total}`,
            answers: answers,
            questions: questions
        };

        onNext({ [dataKey]: assessmentData });
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <h4 className="font-bold text-lg">{title}</h4>
            {questions.length === 0 ? <p className="text-gray-500">Tidak ada kuesioner yang relevan untuk usia anak saat ini.</p> :
                questions.map((item, index) => (
                    <div key={index} className="border p-3 rounded-lg bg-gray-50">
                        <label className="block mb-2 text-gray-700">{index + 1}. {item.q}</label>
                        <select onChange={e => handleAnswer(index, e.target.value)} className="input-field" required>
                            <option value="">Pilih Jawaban</option>
                            <option value="Ya">Ya</option>
                            <option value="Tidak">Tidak</option>
                        </select>
                    </div>
                ))
            }
            <Button type="submit" disabled={questions.length === 0}>{nextStepLabel}</Button>
        </form>
    );
}

// --- Main Assessment Screen Component ---

export default function AssessmentScreen({ user, child, navigate }) {
    const [step, setStep] = useState(1);
    const [assessmentData, setAssessmentData] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const ageInMonths = calculateAgeMonths(child.dob.toDate());

    const determineOverallStatus = (results) => {
        if (results.pertumbuhan?.status === 'Perlu Rujukan Segera') return 'Perlu Rujukan Segera';
        if (results.pemeriksaanMata?.pupilPutih?.status === 'Penyimpangan (Leukokoria)') return 'Perlu Rujukan Segera';
        if (results.chat?.status === 'Risiko Terdeteksi') return 'Ada Penyimpangan';
        if (results.mental?.status === 'Risiko Tinggi' || results.mental?.status === 'Kemungkinan GPPH') return 'Ada Penyimpangan';
        if (results.perkembangan?.status === 'Penyimpangan') return 'Ada Penyimpangan';
        if (results.dayaDengar?.status === 'Kemungkinan Gangguan Pendengaran') return 'Ada Penyimpangan';
        if (results.pemeriksaanMata?.dayaLihat?.status === 'Kemungkinan Gangguan Penglihatan') return 'Ada Penyimpangan';

        if (results.pertumbuhan?.status === 'Perlu Perhatian') return 'Perlu Perhatian';
        if (results.perkembangan?.status === 'Meragukan') return 'Meragukan';
        if (results.mental?.status === 'Risiko Ringan' || results.mental?.status === 'Kemungkinan GPPH Ringan') return 'Perlu Perhatian';

        return 'Sesuai';
    };

    const pushNotificationToFirestore = async (ownerUid, childName, childId, status, details) => {
        try {
            await addDoc(collection(db, `users/${ownerUid}/notifications`), {
                childId: childId,
                title: '⚠️ Peringatan Kritis Pemeriksaan',
                description: `Pemeriksaan ${childName} menunjukkan status "${status}". Segera periksa detail hasil dan lakukan rujukan jika diperlukan.`,
                date: Timestamp.now(),
                icon: '<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>',
                type: 'alert',
                read: false,
                urgency: 'high'
            });
        } catch (error) {
            console.error("Gagal mengirim notifikasi peringatan", error);
        }
    };

    const handleNext = async (data) => {
        const newAssessmentData = { ...assessmentData, ...data };
        setAssessmentData(newAssessmentData);

        if (step < 7) {
            setStep(step + 1);
        } else {
            setIsSaving(true);
            const finalResults = { ...newAssessmentData };
            const overallStatus = determineOverallStatus(finalResults);
            const createdAt = Timestamp.now();

            if (user.isAnonymous) {
                const fullAssessmentData = { id: 'demo-comprehensive-assessment', createdAt, ...finalResults, overallStatus };
                setIsSaving(false);
                navigate('results', child, fullAssessmentData);
                return;
            }

            try {
                const ownerUid = child.ownerUid || user.uid;
                const assessmentsPath = `users/${ownerUid}/children/${child.id}/assessments`;
                
                // Save complete assessment data only at the final step
                const docRef = await addDoc(collection(db, assessmentsPath), {
                    ...finalResults,
                    overallStatus,
                    createdAt,
                    childName: child.name,
                    childDob: child.dob,
                    ageInMonths: ageInMonths
                });

                // Update child's last assessment summary
                const childDocRef = doc(db, `users/${ownerUid}/children/${child.id}`);
                await updateDoc(childDocRef, {
                    lastAssessment: {
                        id: docRef.id,
                        overallStatus: overallStatus,
                        createdAt: createdAt
                    }
                });

                // Push Warning Notification if critical
                if (overallStatus === 'Perlu Rujukan Segera' || overallStatus === 'Ada Penyimpangan') {
                    await pushNotificationToFirestore(ownerUid, child.name, child.id, overallStatus, finalResults);
                }
                
                setIsSaving(false);
                const fullAssessmentData = { id: docRef.id, createdAt, ...finalResults, overallStatus };
                navigate('results', child, fullAssessmentData);

            } catch (error) {
                console.error("Error finalizing assessment:", error);
                alert("Gagal menyimpan hasil pemeriksaan. Silakan coba lagi.");
                setIsSaving(false);
            }
        }
    };

    const renderStep = () => {
        switch (step) {
            case 1: return <PertumbuhanStep onNext={handleNext} child={child} />;
            case 2: 
                const kpspAges = Object.keys(kuesionerData.kpsp).map(Number);
                const closestKpspAge = kpspAges.reduce((prev, curr) => (Math.abs(curr - ageInMonths) < Math.abs(prev - ageInMonths) ? curr : prev));
                return <KuesionerStep onNext={handleNext} title={`Deteksi Perkembangan (KPSP) - Usia ${closestKpspAge} Bulan`} questions={kuesionerData.kpsp[closestKpspAge] || []} dataKey="perkembangan" nextStepLabel="Lanjut ke Mental Emosional" child={child} />;
            case 3:
                const kmmeQuestions = ageInMonths >= 36 ? kuesionerData.kmme : [];
                return <KuesionerStep onNext={handleNext} title={`Deteksi Mental Emosional (KMME)`} questions={kmmeQuestions} dataKey="mental" nextStepLabel="Lanjut ke Skrining GPPH" child={child} />;
            case 4: return <GpphForm child={child} onSave={handleNext} />;
            case 5: return <TddForm child={child} onSave={handleNext} />;
            case 6: return <MataForm child={child} onSave={handleNext} />;
            case 7: return <ChatForm child={child} onSave={handleNext} />;
            default: return null;
        }
    };

    const stepEmojis = ['📏', '🧠', '💭', '⚡', '👂', '👁️', '🧩'];
    const stepTitles = [
        'Pertumbuhan Fisik', 'Perkembangan (KPSP)', 'Mental Emosional (KMME)', 'Skrining GPPH', 'Tes Daya Dengar (TDD)', 'Pemeriksaan Mata', 'Skrining Autisme (CHAT)'
    ];

    return (
        <div className="bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen p-4">
            <div className="max-w-4xl mx-auto">
                {/* Header Card */}
                <div className="bg-white rounded-3xl shadow-xl p-6 mb-6 border border-purple-100">
                    <div className="text-center mb-6">
                        <div className="flex items-center justify-center space-x-3 mb-3">
                            <span className="text-4xl">{stepEmojis[step - 1]}</span>
                            <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                                Tahap {step} dari 7
                            </h2>
                        </div>
                        <p className="text-gray-600 font-medium text-lg">{stepTitles[step - 1]}</p>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="relative">
                        <div className="w-full bg-gradient-to-r from-gray-200 to-gray-300 rounded-full h-4 shadow-inner">
                            <div 
                                className="bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500 h-4 rounded-full shadow-lg transition-all duration-500 ease-out"
                                style={{ width: `${(step / 7) * 100}%` }}
                            ></div>
                        </div>
                        <div className="flex justify-between mt-2">
                            {stepEmojis.map((emoji, index) => (
                                <div 
                                    key={index}
                                    className={`
                                        w-8 h-8 rounded-full flex items-center justify-center text-sm transition-all duration-300
                                        ${index < step ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg scale-110' : index === step - 1 ? 'bg-gradient-to-r from-blue-400 to-cyan-400 text-white shadow-lg scale-125 animate-pulse' : 'bg-gray-200 text-gray-400'}
                                    `}
                                >
                                    {emoji}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Content Card */}
                <div className="bg-white rounded-3xl shadow-xl p-6 border border-purple-100 relative">
                    {isSaving ? (
                        <div className="flex flex-col items-center justify-center py-20 space-y-4">
                            <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
                            <p className="text-indigo-600 font-bold uppercase tracking-wider text-sm">Menyimpan Pemeriksaan & Evaluasi...</p>
                        </div>
                    ) : (
                        renderStep()
                    )}
                </div>
            </div>
        </div>
    );
}
